import React, { Component, Fragment } from 'react'
import { media, ResponsiveContainer } from 'app/assets/styles'
import { colors } from 'shared/assets/styles'
import styled from 'styled-components'
import { H2, H5, SmallText } from 'app/common/typography'
import { FormattedMessage } from 'react-intl'
import VisibilitySensor from 'react-visibility-sensor'
import type { BurgerIngredientImageDTO, IngredientShortDTO } from 'shared/types'
import { Picture } from 'app/common/picture'

type Props = {
    ingredients: Array<IngredientShortDTO>,
    burgerImages: Array<BurgerIngredientImageDTO>
}

type State = {
    animate: boolean
}

class Ingredients extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            animate: false
        }
    }

    onChange = (isVisible: boolean) => {
        if (isVisible && this.props.burgerImages) {
            this.setState(prevState => ({
                animate: true
            }))
        }
    }

    renderTitle() {
        return (
            <StyledH2>
                <FormattedMessage id="product.ingredients.title" />
            </StyledH2>
        )
    }

    renderIngredients(ingredients: Array<IngredientShortDTO>) {
        return (
            <Fragment>
                {ingredients.map((ingredient, index) => (
                    <IngredientContainer key={index}>
                        <StyledH5>{ingredient.title}</StyledH5>
                        <DescriptionWrapper>
                            <StyledText>{ingredient.description}</StyledText>
                        </DescriptionWrapper>
                    </IngredientContainer>
                ))}
            </Fragment>
        )
    }

    render() {
        const { ingredients, burgerImages } = this.props

        let burgers = burgerImages.slice()
        let firstImage = []
        let lastBurger

        // TODO get last image size
        let containerHeight = 150
        const containerMinHeight =
            (Math.floor(ingredients.length / 2) + (ingredients.length % 2)) *
                120 +
            80

        let leftIngredientArray = []
        let rightIngredientArray = []

        if (burgers.length > 0) {
            firstImage = burgers[0].image
            burgers.shift()
        }

        if (burgers.length > 0) {
            lastBurger = burgers[burgers.length - 1]
            if (window.innerWidth < 992) {
                containerHeight += lastBurger.offsetMax * 0.65
            } else {
                containerHeight += lastBurger.offsetMax
            }
        }

        const sortedIngredients = [...ingredients].sort(
            (a, b) => a.displayOrder - b.displayOrder
        )

        sortedIngredients.map(ingredient => {
            return ingredient.displayOrder % 2 === 0
                ? leftIngredientArray.push(ingredient)
                : rightIngredientArray.push(ingredient)
        })

        return (
            <Container minHeight={containerMinHeight} height={containerHeight}>
                <BlockContainer>
                    <LeftBlock>
                        {this.renderTitle()}
                        {this.renderIngredients(leftIngredientArray)}
                    </LeftBlock>
                </BlockContainer>
                <MainBlocContainer height={containerHeight}>
                    <ProductImage>
                        <Picture
                            singleImage
                            imageUrl={firstImage ? firstImage : []}
                        />
                    </ProductImage>
                    {burgers.map((burgerImage, index) => (
                        <PictureContainer
                            key={index}
                            offsetMin={burgerImage.offsetMin}
                            animate={this.state.animate}
                            offset={
                                burgerImage.offsetMax - burgerImage.offsetMin
                            }
                            index={99 - index}>
                            <Picture singleImage imageUrl={burgerImage.image} />
                        </PictureContainer>
                    ))}
                    <VisibilitySensor
                        delayedCall={true}
                        offset={{ bottom: 15 }}
                        onChange={this.onChange}>
                        <PictureContainer
                            offsetMin={
                                lastBurger !== undefined
                                    ? lastBurger.offsetMin
                                    : 0
                            }
                            animate={this.state.animate}
                            offset={
                                lastBurger
                                    ? lastBurger.offsetMax -
                                      lastBurger.offsetMin
                                    : 0
                            }
                            index={50}>
                            <Picture
                                singleImage
                                imageUrl={lastBurger ? lastBurger.image : []}
                            />
                        </PictureContainer>
                    </VisibilitySensor>
                </MainBlocContainer>
                <MobileBlocContainer>
                    <MobileBloc>
                        {this.renderTitle()}
                        {this.renderIngredients(ingredients)}
                    </MobileBloc>
                </MobileBlocContainer>
                <BlockContainer>
                    <RightBloc>
                        {this.renderIngredients(rightIngredientArray)}
                    </RightBloc>
                </BlockContainer>
            </Container>
        )
    }
}

const StyledH2 = styled(H2)`
    color: black;
    margin-left: 0px;
    position: inherit;
    padding-top: 20px;

    ${media.medium`
        padding-top: 0px;
    `};
`

const StyledH5 = styled(H5)`
    border-bottom: solid 1px ${colors.grey30};
    padding-bottom: 10px;
    margin: 0 auto;
`

const StyledText = styled(SmallText)`
    line-height: 28px;
    color: ${colors.grey};
`

const DescriptionWrapper = styled.div`
    padding-top: 10px;
`
const Container = styled(ResponsiveContainer)`
    display: flex;
    flex-wrap: wrap;
    margin: 0 auto;
    flex-direction: column;
    height: auto;
    padding-top: 10px;
    padding-bottom: 10px;

    ${media.medium`
        //height: ${props => (props.height ? props.height : 0)}px;
        min-height: ${props =>
            props.minHeight ? `${props.minHeight}px` : 'unset'};
        padding-top: 40px;
        padding-bottom: 40px;
        flex-direction: row;
        max-width: max-content;
    `};
`

const MainBlocContainer = styled.div`
    margin: auto;
    width: 100%;
    height: ${props => (props.height ? props.height : 0)}px;
    position: relative;

    ${media.medium`
        width: 34%;
    `};
`

const MobileBlocContainer = styled.div`
    display: block;
    width: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;

    ${media.medium`
        display: none;
    `};
`

const BlockContainer = styled.section`
    display: none;

    ${media.medium`
        display: block;
        width: 33%;
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
    `};
`

const LeftBlock = styled.section`
    display: none;

    ${media.medium`
        display:block;
        margin: auto;
        margin-left: 0;
        width: 75%;
    `};
`

const IngredientContainer = styled.section`
    padding-top: 12px;
    padding-bottom: 12px;

    ${media.medium`
        padding-top: 20px;
        padding-bottom: 20px;
    `};
`
const MobileBloc = styled.section`
    display: block;
    width: 100%;
    margin: auto;

    ${media.medium`
        display:none;
    `};
`

const RightBloc = styled.section`
    width: 100%;
    margin: auto;
    margin-right: 0;

    ${media.large`
        padding-left: 0px;
    `};

    ${media.medium`
        width: 75%;
    `};
`

const PictureContainer = styled.div`
    position: absolute;
    width: 100%;
    left: 0;
    right: 0;
    max-width: 300px;
    top: ${props => (props.offsetMin ? props.offsetMin : 0) - 90}px;
    z-index: ${props => (props.index ? props.index : 0)};
    transform: translateY(
        ${props => (props.animate ? props.offset * 0.55 : 0)}px
    );
    transition: transform 1s ease;
    margin: auto;
    ${media.medium`
        transform: translateY(${props => (props.animate ? props.offset : 0)}px);
        min-width: 300px;
    `};
`

const ProductImage = styled.div`
    margin: auto;
    position: relative;
    width: 100%;
    max-width: 300px;
    margin-top: -100px;
    z-index: 100;
    ${media.medium`
        min-width: 300px;
    `};
`

export { Ingredients }
